import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector, shallowEqual } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { DownloadFile } from "./FileApi";
import {
  authenticateDocMgmt,
  getDocMgmtFilePresignedUrlForPath,
  isDocMgmtAuthenticated,
} from "./DocMgmtApi";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    height: "100%",
    maxHeight: "100%",
    margin: 0,
    borderRadius: 0,
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 2.5),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleText: {
    fontSize: "1.1rem",
    fontWeight: 600,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "80%",
  },
  dialogContent: {
    padding: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
    backgroundColor: "#f4f6f8",
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    flexGrow: 1,
  },
  loaderBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    minHeight: "300px",
    gap: theme.spacing(2),
  },
  loaderText: {
    fontSize: "0.95rem",
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
  },
  errorBox: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    textAlign: "center",
  },
  dialogActions: {
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

async function ensureDocMgmtAuth(email) {
  if (isDocMgmtAuthenticated() || !email) return;
  await authenticateDocMgmt(email);
}

/**
 * AccbPdfViewer Component
 *
 * Unified ACCB PDF Viewer that can be used:
 * 1. As a Reusable Modal: Pass `pdfUrl`, `fileId`, or `file` props.
 * 2. As a Route Page Component: Mounted on routes like `/stampinghelper/:workspaceId/:bundle/:tab`,
 *    it automatically extracts URL params, calls Document Management API, and handles goBack on close.
 */

export default function AccbPdfViewer({
  open = true,
  onClose,
  dismiss,
  pdfUrl,
  file,
  fileId,
  fileName,
  title,
}) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const profile = useSelector((state) => state.profile, shallowEqual);
  const userEmail = profile?.profile?.user?.email;

  const [url, setUrl] = useState(pdfUrl || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isRouteMode = location?.pathname?.startsWith("/stampinghelper");

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (dismiss) {
      dismiss();
    } else if (isRouteMode) {
      history.goBack();
    }
  };

  const displayName = title || fileName || file?.name || "PDF Document Viewer";
  const targetFileId = fileId || file?.id;

  useEffect(() => {
    setError(null);

    // Mode 1: Direct pdfUrl prop passed
    if (pdfUrl) {
      setUrl(pdfUrl);
      setLoading(false);
      return;
    }

    // Mode 2: fileId or file object passed
    if (targetFileId) {
      setLoading(true);
      DownloadFile(targetFileId)
        .then((res) => {
          if (res?.data?.url) {
            setUrl(res.data.url);
          } else {
            setError("Document URL not found.");
          }
        })
        .catch((err) => {
          console.error("Error fetching PDF presigned URL:", err);
          setError(
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to load document."
          );
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    // Mode 3: Route Mode (/stampinghelper/:workspaceId/:bundle/:tab...)
    if (isRouteMode) {
      let ar = location.pathname.split("/");
      const workspaceId = ar[2];

      if (ar[3]) {
        let p = "" + ar[3];
        for (let index = 4; index < ar.length; index++) {
          p += "/" + ar[index];
        }
        p = p.toUpperCase();

        if (!isDocMgmtAuthenticated() && !userEmail) {
          setLoading(true);
          return;
        }

        setLoading(true);
        ensureDocMgmtAuth(userEmail)
          .then(() => getDocMgmtFilePresignedUrlForPath(workspaceId, p))
          .then((res) => {
            if (res?.data?.url) {
              setUrl(res.data.url);
            } else {
              setError("Document URL not found.");
            }
          })
          .catch((err) => {
            console.error("Doc mgmt path presigned URL fetch failed:", err);
            setError(
              err?.response?.data?.error ||
              err?.response?.data?.message ||
              "Failed to load document link."
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setError("Invalid document path in URL.");
        setLoading(false);
      }
      return;
    }

    setUrl("");
    setLoading(false);
  }, [pdfUrl, targetFileId, location?.pathname, isRouteMode, userEmail]);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      fullScreen
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.titleText} title={displayName}>
          {displayName}
        </Typography>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loaderBox}>
            <CircularProgress size={40} color="primary" />
            <Typography className={classes.loaderText}>
              Loading PDF document...
            </Typography>
          </Box>
        ) : error ? (
          <Box className={classes.errorBox}>
            <Typography variant="h6" color="error">
              {error}
            </Typography>
            <Button variant="contained" color="primary" onClick={handleClose}>
              {isRouteMode ? "Go Back" : "Close"}
            </Button>
          </Box>
        ) : url ? (
          <iframe src={url} title={displayName} className={classes.iframe} />
        ) : (
          <Box className={classes.errorBox}>
            <Typography variant="body1" color="textSecondary">
              No PDF file provided to view.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button variant="outlined" color="primary" onClick={handleClose}>
          {isRouteMode ? "Go Back" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { AccbPdfViewer };
